import React from 'react';
import { IdsButton } from '@investec/ids-web-react';
import Button from '../../../Form/Button/Button';
import { productTypes } from '../../../../utils/productHelpers';
import { useUnifiedAnalytics } from '../../../../contexts/AnalyticsWrapper';

/**
 * Get the action button for an account based on its product type and status
 * @param {{ 
 *   account: any;
 *   isClosed: boolean;
 *   onStartWithdrawal: () => void;
 *   onStartDeposit: () => void;
 *   onStartTransfer: () => void;
 * }} props
 * @returns {React.ReactNode}
 */
export function useAccountActionButton({ account, isClosed, onStartWithdrawal, onStartDeposit, onStartTransfer }) {
    const analytics = useUnifiedAnalytics();
    const productType = account.product.productType;
    
    if (isClosed) {
        return null;
    }
    
    if (productType === productTypes.FixedTerm) {
        const isMaturing = account.maturity && 
            account.maturity.maturityInstructionWindowStatus === "InstructionOpen";
        const hasInstruction = account.maturity && account.maturity.maturityInstruction;

        if (isMaturing && !hasInstruction) {
            return (
                <Button 
                    size='med'
                    label="View your options" 
                    variant="primary" 
                    onClick={() => {
                        if (analytics) {
                            analytics.trackAnalyticsClickEvent(
                                "view your options",
                                `/maturity-instruction/${account.id}`,
                                "dashboard account card",
                                account.product.code,
                                "maturity",
                                {},
                                "link click",
                                "navigation|click|view your options",
                                "Maturity Instruction"
                            );
                        }
                        window.location.href = `/maturity-instruction/${account.id}`;
                    }} 
                />
            );
        } else if (account.maturity && account.maturity.maturityInstructionWindowStatus && hasInstruction) {
            return (
                <Button 
                    size='med'
                    label="View your instruction" 
                    variant="primary" 
                    onClick={() => {
                        if (analytics) {
                            analytics.trackAnalyticsClickEvent(
                                'view your instruction',
                                `account-details/${account.id}`,
                                'dashboard',
                                account.product.code,
                                '',
                                {},
                                'link click',
                                `dashboard|click|${account.product.code}-view-your-instruction`,
                                'dashboard'
                            );
                        }
                        window.location.href = "account-details/" + account.id;
                    }} 
                />
            );
        }
        return null;
    }
    
    if (productType === productTypes.EasyAccess) {
        return (
            <Button 
                size='med'
                label="Move money" 
                variant="primary" 
                disabled={account.balance === 0} 
                onClick={onStartWithdrawal} 
            />
        );
    }

    if (productType === productTypes.InstantAccessCashISA) {
        const transferOutStatus = String(account.transferOut?.status || '')
            .trim().replace(/[\s_-]+/g, '').toLowerCase();
        // A cancellation request remains active until cancellation is confirmed.
        const hasActiveTransferOut = ['requested', 'inprogress', 'cancellationrequested'].includes(transferOutStatus);
        const isTransferDisabled = hasActiveTransferOut;
        const isWithdrawalDisabled = account.balance === 0 || account.availableBalance === 0 || hasActiveTransferOut;

        return (
            <>
                <IdsButton
                    size="md"
                    appearance="primary"
                    disabled={isTransferDisabled}
                    onClick={() => {
                        if (isTransferDisabled) return;
                        if (analytics) {
                            analytics.trackAnalyticsClickEvent(
                                'transfer your isa',
                                '/new-account/fisa/isa-transfer',
                                'dashboard account card',
                                account.product.code,
                                'isa transfer',
                                {},
                                'button click',
                                'dashboard|click|transfer-your-isa',
                                'dashboard'
                            );
                        }
                        onStartTransfer();
                    }}
                >
                    Transfer your ISA
                </IdsButton>
                <IdsButton
                    size="md"
                    appearance="primary"
                    disabled={isWithdrawalDisabled}
                    onClick={() => {
                        if (isWithdrawalDisabled) return;
                        if (analytics) {
                            analytics.trackAnalyticsClickEvent(
                                'withdraw from your isa',
                                '',
                                'dashboard account card',
                                account.product.code,
                                'isa withdrawal',
                                {},
                                'button click',
                                'dashboard|click|withdraw-from-your-isa',
                                'dashboard'
                            );
                        }
                        onStartWithdrawal(account.id, account.product.productType);
                    }}
                >
                    Withdraw from your ISA
                </IdsButton>
            </>
        );
    }
    
    if (productType === productTypes.Notice) {
        return (
            <Button 
                size='med'
                label="Move money" 
                variant="primary" 
                disabled={account.balance === 0 || account.availableBalance === 0} 
                onClick={onStartWithdrawal} 
            />
        );
    }
    
    if (productType === productTypes.FixedRateCashISA) {
        return (
            <Button 
                size='med'
                label="Manage your account" 
                variant="primary" 
                onClick={() => {
                    if (analytics) {
                        analytics.trackAnalyticsClickEvent(
                            "manage your account",
                            window.location.href,
                            "FISA account card",
                            "fisa_1yr",
                            account.id,
                            {},
                            "button click",
                            "dashboard|click|manage-fisa-account",
                            "dashboard"
                        );
                    }
                    window.location.href = "account-details/" + account.id;
                }} 
            />
        );
    }
    
    return null;
}
